import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { STORAGE_KEYS } from '../../utils/constants';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  ChevronDownIcon,
  HomeIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const { user: contextUser, isAuthenticated, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'About', href: '/about-us', icon: InformationCircleIcon },
    { name: 'FAQ', href: '/faq', icon: QuestionMarkCircleIcon },
    { name: 'Contact', href: '/contact-us', icon: EnvelopeIcon },
  ];

  const serviceItems = [
    { name: 'Service History', href: '/service-history', icon: ClipboardDocumentListIcon },
  ];

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
      if (storedUser) setCurrentUser(JSON.parse(storedUser));
    } catch {}
  }, [contextUser, isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentUser(null);
      navigate('/');
    } catch {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      setCurrentUser(null);
      navigate('/');
    }
  };

  const getUserDisplayName = () => {
    const user = currentUser || contextUser;
    if (!user) return 'Guest';
    const userData = user.data || user;
    if (userData.name?.trim() && userData.name !== 'User') return userData.name.trim();
    if (userData.phoneNumber) return userData.phoneNumber;
    if (userData.email) return userData.email.split('@')[0];
    return 'User';
  };

  const getUserInitials = () => {
    const displayName = getUserDisplayName();
    if (displayName === 'Guest' || displayName === 'User') return 'U';
    const words = displayName.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return displayName.substring(0, 2).toUpperCase();
  };

  const getUserProfileImage = () => {
    const user = currentUser || contextUser;
    return (user?.data || user)?.profile || null;
  };

  const user = currentUser || contextUser;
  const profileImage = getUserProfileImage();
  const displayName = getUserDisplayName();
  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#111111]/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.4)] border-b border-white/5'
          : 'bg-[#111111] border-b border-white/5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-18">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
            <img
              src="/logo.png"
              alt="OkMechanic"
              className="h-8 sm:h-9 w-auto transition-transform duration-300 group-hover:scale-105"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="hidden sm:block text-white font-bold text-lg tracking-tight group-hover:text-[#FF6B35] transition-colors duration-200">
              Ok<span className="text-[#FF6B35]">Mechanics</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    active
                      ? 'text-[#FF6B35] bg-[#FF6B35]/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? 'text-[#FF6B35]' : ''}`} />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Desktop Right Side */}
          <div className="hidden md:flex items-center gap-3">
            {loading ? (
              <div className="flex items-center gap-2 px-4 py-2">
                <div className="w-4 h-4 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            ) : isAuthenticated && user ? (
              <div className="flex items-center gap-2">
                {/* Service quick links */}
                {serviceItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      title={item.name}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isActive(item.href)
                          ? 'bg-[#FF6B35]/15 text-[#FF6B35]'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                    </Link>
                  );
                })}

                <div className="w-px h-6 bg-white/10 mx-1" />

                {/* User dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={`flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-xl transition-all duration-200 ${
                      isUserMenuOpen ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="relative">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt={displayName}
                          className="h-8 w-8 rounded-full object-cover ring-2 ring-[#FF6B35]/40"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#E85520] flex items-center justify-center text-white font-bold text-xs shadow-md">
                          {getUserInitials()}
                        </div>
                      )}
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-[#111111] rounded-full" />
                    </div>
                    <div className="hidden xl:block text-left">
                      <p className="text-sm font-semibold text-white leading-tight">{displayName}</p>
                      <p className="text-xs text-gray-500">Account</p>
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-[#1C1C1C] rounded-xl shadow-2xl border border-white/10 overflow-hidden">
                      {/* Header */}
                      <div className="p-4 bg-gradient-to-br from-[#FF6B35]/10 to-transparent border-b border-white/10">
                        <div className="flex items-center gap-3">
                          {profileImage ? (
                            <img src={profileImage} alt={displayName} className="h-10 w-10 rounded-full object-cover ring-2 ring-[#FF6B35]/40" />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#E85520] flex items-center justify-center text-white font-bold text-sm">
                              {getUserInitials()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-white text-sm">{displayName}</p>
                            <p className="text-xs text-gray-500">Logged in</p>
                          </div>
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="p-2">
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <div className="p-1.5 bg-[#FF6B35]/10 rounded-lg">
                            <UserIcon className="h-4 w-4 text-[#FF6B35]" />
                          </div>
                          My Profile
                        </Link>

                        {serviceItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <div className="p-1.5 bg-white/5 rounded-lg">
                                <Icon className="h-4 w-4 text-gray-400" />
                              </div>
                              {item.name}
                            </Link>
                          );
                        })}

                        <div className="my-1.5 border-t border-white/10" />

                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors w-full"
                        >
                          <div className="p-1.5 bg-red-500/10 rounded-lg">
                            <ArrowRightOnRectangleIcon className="h-4 w-4 text-red-400" />
                          </div>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-semibold text-gray-300 hover:text-white rounded-lg transition-all duration-200 hover:bg-white/5"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 text-sm font-bold text-white bg-[#FF6B35] hover:bg-[#E85520] rounded-lg transition-all duration-200 shadow-lg shadow-[#FF6B35]/20 hover:shadow-[#FF6B35]/30 hover:-translate-y-0.5"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors focus:outline-none"
              aria-label="Toggle navigation menu"
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center gap-1.5">
                <span className={`block w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`block w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                <span className={`block w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[64px] bg-[#111111] z-40 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">

            {/* Navigation links */}
            <div className="space-y-1">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 px-2">Menu</p>
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-semibold transition-all ${
                      active
                        ? 'bg-[#FF6B35] text-white shadow-lg shadow-[#FF6B35]/20'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${active ? 'bg-white/20' : 'bg-white/5'}`}>
                      <Icon className={`h-5 w-5 ${active ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Services section */}
            {isAuthenticated && user && (
              <div className="space-y-1">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3 px-2">My Services</p>
                {serviceItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-4 px-4 py-3.5 rounded-xl text-base font-semibold transition-all ${
                        active ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-gray-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${active ? 'bg-[#FF6B35]/10' : 'bg-white/5'}`}>
                        <Icon className={`h-5 w-5 ${active ? 'text-[#FF6B35]' : 'text-gray-400'}`} />
                      </div>
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* User section */}
            <div className="pt-4 border-t border-white/10">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
                </div>
              ) : isAuthenticated && user ? (
                <div className="space-y-3">
                  <Link
                    to="/profile"
                    className="flex items-center gap-4 p-4 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/20"
                  >
                    {profileImage ? (
                      <img src={profileImage} alt={displayName} className="h-12 w-12 rounded-full object-cover ring-2 ring-[#FF6B35]/40" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#E85520] flex items-center justify-center text-white font-bold text-base shadow-md">
                        {getUserInitials()}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-bold text-white">{displayName}</p>
                      <p className="text-sm text-[#FF6B35] font-medium">View Profile →</p>
                    </div>
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-xl text-base font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    className="block w-full px-4 py-3.5 rounded-xl text-base font-bold text-gray-300 bg-white/5 hover:bg-white/10 transition-colors text-center border border-white/10"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="block w-full px-4 py-3.5 rounded-xl text-base font-bold text-white bg-[#FF6B35] hover:bg-[#E85520] transition-all text-center shadow-lg shadow-[#FF6B35]/20"
                  >
                    Get Started Free
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
